import React, { FunctionComponent } from "react";
import {
  makePlanLongId,
  shouldSuppressDrugPricing,
  shouldSuppressPlanBenefits,
  useFormatMessage,
  useTranslate,
} from "../../helpers";
import { Plan } from "../../@types";
import { EmptyTableField } from "../../components";
import { PharmacyNetworkLabelHelpDrawer } from "../../components/HelpDrawers/PharmacyNetworkLabelHelpDrawer";
import { AlertMessageConditions } from "../../app/contexts/AlertMessaging/types";

export type DrugCostsProps = {
  coveredDrugs: JSX.Element[] | null;
  displayMessageAfterTotalDrugCosts: boolean;
  displayMessageInDrugsCoveredRow: boolean;
  displayMessageInTotalDrugCostsRow: boolean;
  generalAlertMessages: Record<string, JSX.Element | null>;
  hasPrescriptions: boolean;
  isYearOverYear: boolean;
  networkStatusAlertMessages: Record<string, JSX.Element | null>;
  plans: Plan[];
  selectedPlan: Plan | undefined;
  totalDrugCosts: JSX.Element[] | null;
  // key = long plan id
  // value = boolean indicating if a plan covers preferred pharmacies or not
  plansPreferredPharmacyStatus?: Record<string, boolean>;
};

export const DrugCosts: FunctionComponent<DrugCostsProps> = ({
  coveredDrugs,
  displayMessageAfterTotalDrugCosts,
  displayMessageInDrugsCoveredRow,
  displayMessageInTotalDrugCostsRow,
  generalAlertMessages,
  hasPrescriptions,
  isYearOverYear,
  networkStatusAlertMessages,
  plans,
  selectedPlan,
  totalDrugCosts,
  plansPreferredPharmacyStatus = {},
}) => {
  const t = useTranslate();

  const formatMessage = useFormatMessage();
  const hasNetworkStatusMessages =
    Object.values(networkStatusAlertMessages).filter(message => message)
      .length > 0;

  const hasAlertToDisplayAfterDrugCosts =
    hasNetworkStatusMessages ||
    (Object.values(generalAlertMessages).filter(message => message).length >
      0 &&
      displayMessageAfterTotalDrugCosts);

  const getMessageToShow = (planId: string) => {
    if (hasNetworkStatusMessages) {
      const planAlertMessage = networkStatusAlertMessages[planId];
      if (planAlertMessage) {
        // suppress preferred message if the plan does not cover preferred pharmacies
        const noPreferredSavedPharmaciesAlertCondition =
          planAlertMessage.props.condition ===
          AlertMessageConditions.NO_PREFERRED_SAVED_PHARMACIES;
        const planCoversPreferredPharmacy =
          plansPreferredPharmacyStatus[planId];
        if (
          noPreferredSavedPharmaciesAlertCondition &&
          !planCoversPreferredPharmacy
        ) {
          return <EmptyTableField />;
        }
        // Show alert message only for plans that have one
        return networkStatusAlertMessages[planId];
      }
      return <EmptyTableField />;
    }
    // Other alert messages are repeated for each plan, with plan context
    return generalAlertMessages[planId];
  };

  return (
    <>
      <tr>
        <td
          className="PlanComparePage__tableHeader"
          colSpan={selectedPlan ? 1 : plans.length + 1}
        >
          {t("plan_terms.drug_coverage_and_costs")}
        </td>
      </tr>
      {((coveredDrugs && hasPrescriptions) ||
        displayMessageInDrugsCoveredRow) && (
        <tr>
          <td className="PlanComparePage__tableLabelColumn">
            <span className="PlanComparePage__tableColumnLabel">
              {t("plan_terms.drugs_covered")}
            </span>
          </td>
          {coveredDrugs}
        </tr>
      )}
      {((totalDrugCosts && hasPrescriptions) ||
        displayMessageInTotalDrugCostsRow) && (
        <tr>
          <td className="PlanComparePage__tableLabelColumn">
            <span className="PlanComparePage__tableColumnLabel">
              {t("plan_details.total_yearly_drug_and_premium_cost")}{" "}
              {isYearOverYear
                ? t("yearly_cost.for_12_month_period")
                : formatMessage(
                    { id: "yearly_cost.year_context" },
                    { year: plans[0].contract_year }
                  )}
            </span>
            {totalDrugCosts && hasPrescriptions && (
              <PharmacyNetworkLabelHelpDrawer
                toggleClassName="ds-u-margin-top--3 ds-u-text-align--left"
                toggleText={t("pharmacy_network_label.help_drawer.title")}
                analyticText="How do pharmacy networks affect what I pay"
              />
            )}
          </td>
          {totalDrugCosts}
        </tr>
      )}
      {hasAlertToDisplayAfterDrugCosts && (
        <tr>
          <td className="PlanComparePage__tableLabelColumn">
            <EmptyTableField />
          </td>
          {plans.map(plan => {
            const planId = makePlanLongId(plan);
            const hasSuppressions =
              shouldSuppressPlanBenefits(plan.redactions) ||
              shouldSuppressDrugPricing(plan.redactions);
            return (
              <td
                key={planId}
                className={`${
                  selectedPlan?.id === plan.id
                    ? " PlanComparePage__selectedCell"
                    : ""
                } foobar`}
              >
                {hasSuppressions ? (
                  <EmptyTableField />
                ) : (
                  getMessageToShow(planId)
                )}
              </td>
            );
          })}
        </tr>
      )}
    </>
  );
};
